// Google Analytics 4 (gtag.js) integration.
//
// Loaded from my_main.ts, which is injected into EVERY page (core Firebase pages
// and the R2-served content pages alike), so a single init here covers the whole
// site — including the commented-bible pages whose verse content is generated
// client-side after a redirect to /commented_bible/?concordance=...&search=...
//
// gtag.js and its collect requests are cross-origin (googletagmanager.com /
// google-analytics.com), so the R2 service worker never intercepts them.

// GA4 Measurement ID. Hardcoded (not read from firebaseConfig) on purpose: dev and
// prod are different Firebase projects but must report into the SAME GA4 property,
// so both deployments use this one ID. Get it from GA Admin -> Data Streams -> Web.
export const GA_MEASUREMENT_ID = "G-N4D03T11P0";

type GtagArgs = [string, ...unknown[]];

declare global {
	interface Window {
		dataLayer?: GtagArgs[];
		gtag?: (...args: unknown[]) => void;
	}
}

function hasValidId(): boolean {
	return /^G-[A-Z0-9]+$/.test(GA_MEASUREMENT_ID);
}

let initialized = false;

/**
 * Loads gtag.js and configures GA4. Safe to call once per page load.
 * Each real page load (this site uses full navigations/redirects, not an SPA)
 * fires its own pageview automatically via the initial config.
 */
export function initAnalytics(): void {
	if (initialized) return;
	if (!hasValidId()) {
		console.info("[analytics] GA_MEASUREMENT_ID not set; analytics disabled");
		return;
	}
	if (typeof window === "undefined" || typeof document === "undefined") return;

	initialized = true;

	window.dataLayer = window.dataLayer || [];
	window.gtag = function gtag(...args: unknown[]) {
		// gtag pushes its raw arguments object onto the dataLayer.
		window.dataLayer!.push(args as GtagArgs);
	};

	window.gtag("js", new Date());
	// Default config sends the initial pageview for this page load.
	window.gtag("config", GA_MEASUREMENT_ID);

	const s = document.createElement("script");
	s.async = true;
	s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
	document.head.appendChild(s);
}

/**
 * Fire a custom event. No-op if analytics is disabled/not yet loaded.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
	if (!hasValidId() || typeof window === "undefined" || !window.gtag) return;
	window.gtag("event", name, params);
}

/**
 * Track a generated commented-bible view. Called after the bible page resolves a
 * concordance topic or verse search and renders the generated verse content, so
 * you can see which topics/passages are actually being read (beyond the raw
 * page_location GA already records for the redirected URL).
 */
export function trackBibleView(params: {
	concordance?: string;
	search?: string;
	book?: string;
}): void {
	const clean = Object.fromEntries(
		Object.entries(params).filter(([, v]) => v != null && v !== ""),
	);
	trackEvent("bible_view", clean);
}
